/**
 * Per-workspace sync schedules, read/written directly against Supabase — the
 * sync_schedules table is master-only via RLS (is_platform_master), so a
 * non-master simply gets nothing back and writes are refused in Postgres. No
 * client-side guard belongs here; the DB is the boundary.
 *
 * @module services/syncScheduleService
 */

import { getSupabaseClient } from "@/config/supabase";
import { logError } from "@/services/logger";

export interface SyncScheduleRow {
  workspaceId: string;
  intervalMinutes: number;
  enabled: boolean;
  lastRunAt: string | null;
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export const MIN_INTERVAL_MINUTES = 15;

/** All schedules, keyed by workspace id (workspaces without a row are absent). */
export const listSyncSchedules = async (): Promise<ServiceResult<Record<string, SyncScheduleRow>>> => {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from("sync_schedules")
      .select("workspace_id, interval_minutes, enabled, last_run_at");
    if (error) return { success: false, error: error.message };

    const map: Record<string, SyncScheduleRow> = {};
    for (const r of (data ?? []) as Array<{
      workspace_id: string;
      interval_minutes: number;
      enabled: boolean;
      last_run_at: string | null;
    }>) {
      map[r.workspace_id] = {
        workspaceId: r.workspace_id,
        intervalMinutes: r.interval_minutes,
        enabled: r.enabled,
        lastRunAt: r.last_run_at,
      };
    }
    return { success: true, data: map };
  } catch (e) {
    logError("SyncSchedule", "list failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to load schedules" };
  }
};

/** Create or update a workspace's schedule (upsert on workspace_id). */
export const saveSyncSchedule = async (
  workspaceId: string,
  updates: { intervalMinutes: number; enabled: boolean },
): Promise<ServiceResult<void>> => {
  if (!Number.isFinite(updates.intervalMinutes) || updates.intervalMinutes < MIN_INTERVAL_MINUTES) {
    return { success: false, error: `Interval must be at least ${MIN_INTERVAL_MINUTES} minutes` };
  }
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from("sync_schedules").upsert({
      workspace_id: workspaceId,
      interval_minutes: Math.round(updates.intervalMinutes),
      enabled: updates.enabled,
      updated_at: new Date().toISOString(),
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (e) {
    logError("SyncSchedule", "save failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to save schedule" };
  }
};
